import { useGetIdentity, useGetManyAggregate, type Identifier } from "ra-core";
import type { ReactNode } from "react";

import type { Sale } from "../types";
import { TeamContactActions } from "./TeamContactActions";

export interface ResponsableWithContactProps {
  /** The responsable's sales id; nothing renders without one. */
  salesId?: Identifier | null;
  /** Already-known display name. When omitted it is looked up from `sales`. */
  name?: string;
  /** Already-known CRM email. When omitted it comes from the `sales` lookup. */
  email?: string | null;
  /** Replaces the plain name (e.g. a "followed by" sentence). */
  children?: ReactNode;
}

/**
 * Almikia Epic EC2c (TASK-134): the responsable's name plus the three
 * quick-contact actions (WhatsApp, Llamar, Correo), identical on leads,
 * contacts, operations and inventory. The actions are left out when the
 * responsable is the current user or has no `sales` row; `TeamContactActions`
 * itself hides the buttons that lack a phone/email and renders nothing for a
 * role-less identity. Inside a `TeamContactsProvider` (list rows) phones come
 * from the page-wide batch. Display-only: never used by exports, vCards or logs.
 */
export const ResponsableWithContact = ({
  salesId,
  name,
  email,
  children,
}: ResponsableWithContactProps) => {
  const { identity } = useGetIdentity();
  const hasSalesId = salesId != null;
  const isCurrentUser =
    hasSalesId &&
    identity?.id != null &&
    String(identity.id) === String(salesId);
  const needsLookup = hasSalesId && name === undefined;
  const { data } = useGetManyAggregate<Sale>(
    "sales",
    { ids: hasSalesId ? [salesId] : [] },
    { enabled: needsLookup },
  );
  if (!hasSalesId) return null;

  const sale = data?.[0];
  const displayName =
    name ?? (sale ? `${sale.first_name} ${sale.last_name}`.trim() : "");
  // With a caller-supplied name the row exists (it was just resolved); with a
  // lookup, a missing row means an unknown sales id.
  const hasSalesRow = name !== undefined || sale != null;
  const contactEmail = email ?? sale?.email;

  return (
    <div className="flex flex-col gap-2">
      {children ?? <span className="text-sm">{displayName}</span>}
      {hasSalesRow && !isCurrentUser ? (
        <TeamContactActions salesId={salesId} email={contactEmail} />
      ) : null}
    </div>
  );
};
